import React, { useState, useEffect } from "react";
import { CardWrapper } from "../../../../components/wrapper";
import AddPushNotificationForm from "../../../../components/parts/forms/addPushNotificationForm";
import jwtInterceoptor from "../../../../api/jwtInterceptor";
import { FormErrorPopup, ConfirmPopup } from "../../../../components/parts/popups";
import { handleApiErrors, showToast } from "../../../../utils/helpers";
import { Spinner } from "react-bootstrap";
import { format } from "date-fns";
import { Button } from "../../../../components/ui";
import tableStyles from "../../../../components/parts/tables/style.module.scss";
import styles from "./style.module.scss";

const PushNotification = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [fetchingNotifications, setFetchingNotifications] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ show: false, id: null });

  // Fetch notifications when component mounts
  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setFetchingNotifications(true);
    try {
      const response = await jwtInterceoptor.get('/api/admin/notifications/schedule/');
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      const errorMessages = handleApiErrors(error);
      setError(errorMessages.map(({ message }) => message));
    } finally {
      setFetchingNotifications(false);
    }
  };

  const openDeleteConfirmation = (id) => {
    setConfirmDelete({ show: true, id });
  };

  const closeDeleteConfirmation = () => {
    setConfirmDelete({ show: false, id: null });
  };

  const handleDeleteNotification = async () => {
    if (!confirmDelete.id) return;
    
    setDeleteLoading(true);
    try {
      await jwtInterceoptor.delete(`/api/admin/notifications/schedule/${confirmDelete.id}/`);
      showToast("Notification deleted successfully", "success");
      // Refresh notifications list
      fetchNotifications();
    } catch (error) {
      console.error("Error deleting notification:", error);
      const errorMessages = handleApiErrors(error);
      setError(errorMessages.map(({ message }) => message));
    } finally {
      setDeleteLoading(false);
      closeDeleteConfirmation();
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      // Convert to PST timezone (UTC-8)
      return format(new Date(date.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div>
      <CardWrapper>
        <h4 className="mb-4">Schedule Push Notification</h4>
        <AddPushNotificationForm
          loading={loading}
          setError={setError}
          setLoading={setLoading}
          onSuccess={fetchNotifications}
        />
      </CardWrapper>

      <CardWrapper className="mt-4">
        
        <div className={tableStyles.tableContainer}>
          {fetchingNotifications && notifications.length === 0 ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : notifications.length === 0 ? (
            <p className="text-center py-3">No notifications scheduled yet.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Message</th>
                  <th>Scheduled For</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((notification) => (
                  <tr key={notification.id}>
                    <td>
                      {notification.image && (
                        <img 
                          src={notification.image} 
                          alt="Notification" 
                          className={styles.notificationImage}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/60x60?text=NA';
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <h5>{notification.caption}</h5>
                    </td>
                    <td>
                      <p>{notification.message}</p>
                    </td>
                    <td>
                      <p>{formatDate(notification.scheduled_at)}</p>
                    </td>
                    <td>
                      <p className={notification.sent ? styles.activeStatus : styles.inactiveStatus}>
                        {notification.sent ? "Active" : "Inactive"}
                      </p>
                    </td>
                    <td>
                      <div className={tableStyles.btnContainer}>
                        <Button 
                          label="Delete"
                          primary
                          onClick={() => openDeleteConfirmation(notification.id)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {deleteLoading && (
                  <div className={tableStyles.tableLoader}>
                    <Spinner animation="border" variant="primary" />
                  </div>
                )}
              </tbody>
            </table>
          )}
        </div>
      </CardWrapper>
      
      {/* Confirmation Dialog */}
      <ConfirmPopup 
        show={confirmDelete.show}
        title="Delete Notification"
        message="Are you sure you want to delete this notification? This action cannot be undone."
        onConfirm={handleDeleteNotification}
        onCancel={closeDeleteConfirmation}
        loading={deleteLoading}
      />
      
      {error && <FormErrorPopup error={error} close={() => setError(false)} />}
    </div>
  );
};

export default PushNotification;
